/**
 * The release sitting's queue, filters and selection (13-12, ISS-167). Pure: no React, no I/O.
 *
 *   - `releaseQueue(rows, actor)`: open and unreleased, through 13-05's `lensFor(actor).releaseQueue`
 *     (reused, not re-implemented). Oversight only: any other lens gets an empty queue.
 *   - `notReleasable(rows)`: unreleased rows that are NOT in the queue, each with a short reason, so
 *     the table says why instead of hiding them (ISS-187: Done rows with no `ReleasedAt` go to Verify;
 *     release only accepts open statuses, and that rule is not changed here). Retired rows are left
 *     out: retiring is how a row leaves every queue on purpose.
 *   - `applyFilters`, `facetCounts`, `clusterHint`: the header's filters, chips and clusters.
 *   - `resolveSelection`: React Aria's `"all"` becomes the FILTERED ids only. This decides which
 *     live rows get written: select-all under a filter means the visible set, never every row.
 *   - `ownerBreakdown`, `pastDue`, `resultParts`: what the confirm and the result line say.
 */

import { lensFor } from './actionAuthority';
import { calendarDaysSince } from './vlVerbs';
import type {
  ActionKind,
  ActionRow,
  ActionSource,
  ActionStatus,
  Actor,
  Executor,
  OwnerRole,
} from '../types/actionRegister';

// ---------------------------------------------------------------------------
// The queue, and the rows that are unreleased but cannot be released
// ---------------------------------------------------------------------------

/** Open and unreleased (`lensFor(actor).releaseQueue`). Empty for anyone who is not Oversight. */
export function releaseQueue(rows: readonly ActionRow[], actor: Actor): ActionRow[] {
  const inQueue = lensFor(actor).releaseQueue;
  return rows.filter((r) => inQueue(r));
}

/** Why an unreleased, non-open row is not releasable. `Retired` is deliberately absent. */
export const NOT_RELEASABLE_REASON: Readonly<Partial<Record<ActionStatus, string>>> = {
  Done: 'Already done — goes to Verify',
  Verified: 'Already verified — nothing to release',
  Rejected: 'Rejected — closed, nothing to release',
  NotMine: 'Handed back — Oversight reassigns it',
};

export interface HeldRow {
  row: ActionRow;
  reason: string;
}

/** Unreleased rows the sitting cannot release, with their reason (shown, never hidden). */
export function notReleasable(rows: readonly ActionRow[]): HeldRow[] {
  const out: HeldRow[] = [];
  for (const row of rows) {
    if (row.releasedAt && row.releasedAt.trim() !== '') continue;
    const reason = NOT_RELEASABLE_REASON[row.status];
    if (reason) out.push({ row, reason });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Age
// ---------------------------------------------------------------------------

/** Melbourne calendar days since the row was created (null when `Created` is unreadable). */
export function ageDays(row: Pick<ActionRow, 'created'>, now: number): number | null {
  return calendarDaysSince(row.created, now);
}

// ---------------------------------------------------------------------------
// Clusters: batches that arrived together, e.g. one audit's findings
// ---------------------------------------------------------------------------

/**
 * The batch part of a `ParentId`: drop a last `-` segment that carries a digit
 * (`AUD-SYN-12` → `AUD-SYN`; `FA-2026-09-02-G12` → `FA-2026-09-02`). An id with no such segment
 * is its own prefix (`MTG-2026-09-09-SyntheticMeeting`). A bare number (`98`, an incident item id)
 * or a blank has no prefix: those rows are not clustered.
 */
export function parentPrefix(parentId: string | null | undefined): string | null {
  const id = (parentId ?? '').trim();
  if (!id) return null;
  const parts = id.split('-');
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1];
  return /\d/.test(last) ? parts.slice(0, -1).join('-') : id;
}

/** The cluster key a row belongs to (`<Source>|<prefix>`), or null. */
export function clusterKey(row: Pick<ActionRow, 'source' | 'parentId'>): string | null {
  const prefix = parentPrefix(row.parentId);
  return prefix === null ? null : `${row.source}|${prefix}`;
}

export interface ClusterHint {
  /** `<Source>|<prefix>`: pass as `filters.cluster`. */
  key: string;
  source: ActionSource;
  prefix: string;
  count: number;
  /** The earliest `Created` in the cluster (ISO), for "from 2 Sep". */
  firstCreated: string;
}

/** Groups of two or more rows sharing `(Source, ParentId prefix)`, largest first. */
export function clusterHint(rows: readonly ActionRow[]): ClusterHint[] {
  const groups = new Map<string, ClusterHint>();
  for (const row of rows) {
    const key = clusterKey(row);
    if (key === null) continue;
    const g = groups.get(key);
    if (g) {
      g.count += 1;
      if ((Date.parse(row.created) || Infinity) < (Date.parse(g.firstCreated) || Infinity)) g.firstCreated = row.created;
    } else {
      groups.set(key, { key, source: row.source, prefix: parentPrefix(row.parentId) as string, count: 1, firstCreated: row.created });
    }
  }
  return [...groups.values()].filter((g) => g.count >= 2).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

// ---------------------------------------------------------------------------
// Filters and facets
// ---------------------------------------------------------------------------

export interface ReleaseFilters {
  source?: ActionSource | '';
  kind?: ActionKind | '';
  ownerRole?: OwnerRole | '';
  executor?: Executor | '';
  /** Strictly older than this many Melbourne calendar days since Created. 0 / null = any age. */
  olderThanDays?: number | null;
  /** Case-insensitive, over the title, ACT ref, SourceRef, ParentId and description. */
  text?: string;
  /** A `clusterHint` key. */
  cluster?: string;
}

function haystack(row: ActionRow): string {
  return [row.title, row.reference, row.sourceRef, row.parentId, row.description].filter(Boolean).join('\n').toLowerCase();
}

/** Every filter must match. Order is preserved. */
export function applyFilters(rows: readonly ActionRow[], f: ReleaseFilters, now: number): ActionRow[] {
  const text = (f.text ?? '').trim().toLowerCase();
  const older = f.olderThanDays ?? 0;
  return rows.filter((r) => {
    if (f.source && r.source !== f.source) return false;
    if (f.kind && r.kind !== f.kind) return false;
    if (f.ownerRole && r.ownerRole !== f.ownerRole) return false;
    if (f.executor && r.executor !== f.executor) return false;
    if (f.cluster && clusterKey(r) !== f.cluster) return false;
    if (older > 0) {
      const age = ageDays(r, now);
      if (age === null || age <= older) return false;
    }
    if (text && !haystack(r).includes(text)) return false;
    return true;
  });
}

export interface Facet<T extends string> {
  value: T;
  count: number;
}

export interface FacetCounts {
  source: Facet<ActionSource>[];
  kind: Facet<ActionKind>[];
  ownerRole: Facet<OwnerRole>[];
  executor: Facet<Executor>[];
}

function facet<T extends string>(rows: readonly ActionRow[], pick: (r: ActionRow) => T): Facet<T>[] {
  const counts = new Map<T, number>();
  for (const r of rows) counts.set(pick(r), (counts.get(pick(r)) ?? 0) + 1);
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** The chips' counts ("Audit 74 · Meeting 31 …"). Each facet sums to `rows.length`. */
export function facetCounts(rows: readonly ActionRow[]): FacetCounts {
  return {
    source: facet(rows, (r) => r.source),
    kind: facet(rows, (r) => r.kind),
    ownerRole: facet(rows, (r) => r.ownerRole),
    executor: facet(rows, (r) => r.executor),
  };
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/** The table's row key for an item id. */
export const rowKey = (id: number): string => `r:${id}`;

function idOfKey(key: string | number): number | null {
  if (typeof key === 'number') return Number.isInteger(key) ? key : null;
  const m = /^r:(\d+)$/.exec(key) ?? /^(\d+)$/.exec(key);
  return m ? Number(m[1]) : null;
}

/**
 * The ids a selection means, in the filtered rows' order. `"all"` = every FILTERED row (never
 * the whole register). A key set is intersected with the filtered rows too, so a key for a row
 * that a filter now hides is never written.
 */
export function resolveSelection(
  selection: 'all' | Iterable<string | number>,
  filteredRows: readonly Pick<ActionRow, 'id'>[],
): number[] {
  if (selection === 'all') return filteredRows.map((r) => r.id);
  const wanted = new Set<number>();
  for (const k of selection) {
    const id = idOfKey(k);
    if (id !== null) wanted.add(id);
  }
  return filteredRows.filter((r) => wanted.has(r.id)).map((r) => r.id);
}

// ---------------------------------------------------------------------------
// The confirm and the result line
// ---------------------------------------------------------------------------

/** How many rows each OwnerRole will be carded for, largest first. */
export function ownerBreakdown(rows: readonly ActionRow[]): Facet<OwnerRole>[] {
  return facet(rows, (r) => r.ownerRole);
}

/** Rows whose DueDate falls on a Melbourne calendar day before today's. */
export function pastDue(rows: readonly ActionRow[], now: number): ActionRow[] {
  return rows.filter((r) => {
    if (!r.dueDate) return false;
    const t = Date.parse(r.dueDate);
    if (Number.isNaN(t) || t >= now) return false;
    return (calendarDaysSince(r.dueDate, now) ?? 0) >= 1;
  });
}

export interface ResultCounts {
  verb: 'release' | 'retire';
  applied: number;
  conflicts: number;
  /** PATCHes re-sent after a 429 / 503 (informational: they may since have landed). */
  retried: number;
  /** Not written and safe to retry (includes still-throttled rows). */
  failed: number;
  /** Refused before sending. */
  refused: number;
  /** Audit rows that did not post for an applied row. */
  auditPending: number;
}

/**
 * The result line's parts ("187 released", "2 changed by someone else", "1 to retry", …). The
 * applied count always comes first, and every exception is named, so a partial failure can never
 * read as a clean success.
 */
export function resultParts(c: ResultCounts): string[] {
  const parts = [`${c.applied} ${c.verb === 'release' ? 'released' : 'retired'}`];
  if (c.conflicts) parts.push(`${c.conflicts} changed by someone else`);
  if (c.failed) parts.push(`${c.failed} to retry`);
  if (c.refused) parts.push(`${c.refused} not sent`);
  if (c.auditPending) parts.push(`${c.auditPending} audit ${c.auditPending === 1 ? 'row' : 'rows'} still to post`);
  if (c.retried) parts.push(`${c.retried} resent after a slow-down`);
  return parts;
}

/**
 * The dev-only `?simulate=` flag for the practice sitting: `412[:n]`, `429[:n]`, `audit[:n]`,
 * comma-separated (defaults 2 / 1 / 1, the mock's "2 changed by someone else · 1 to retry").
 * Returns null when nothing valid is asked for. The caller passes the result to the PRACTICE
 * writer only (`commitAdapter`'s `PracticeSimulation`), and only in a dev build.
 */
export function parseSimulateFlag(
  value: string | null | undefined,
): { conflicts?: number; throttled?: number; auditFail?: number } | null {
  if (!value) return null;
  const out: { conflicts?: number; throttled?: number; auditFail?: number } = {};
  for (const token of value.split(',')) {
    const m = /^\s*(412|429|audit)(?::(\d{1,3}))?\s*$/i.exec(token);
    if (!m) continue;
    const kind = m[1].toLowerCase();
    const n = m[2] !== undefined ? Number(m[2]) : kind === '412' ? 2 : 1;
    if (kind === '412') out.conflicts = n;
    else if (kind === '429') out.throttled = n;
    else out.auditFail = n;
  }
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// ISS-212: the note says one verb, the button pressed was the other
// ---------------------------------------------------------------------------

/** The word stem that names each sitting verb in a note (`\b`: "unreleased" is not "release"). */
export const NOTE_VERB_PATTERN: Readonly<Record<'release' | 'retire', RegExp>> = {
  release: /\breleas/i,
  retire: /\bretir/i,
};

/**
 * ISS-212 (Andy's 4 Oct misclick: "Smoke test, retire", then Release): the verb the note names
 * when it is NOT the one pressed, or null. Release with a note matching `/\bretir/i` → 'retire';
 * Retire with a note matching `/\breleas/i` → 'release'. The confirm then asks "did you mean…?".
 */
export function noteVerbMismatch(note: string, pressed: 'release' | 'retire'): 'release' | 'retire' | null {
  const other = pressed === 'release' ? 'retire' : 'release';
  return NOTE_VERB_PATTERN[other].test(note) ? other : null;
}

/** ISS-212: the guard's question, e.g. "Your note says 'retire' — did you mean Retire?". */
export function mismatchQuestion(other: 'release' | 'retire'): string {
  const title = other === 'release' ? 'Release' : 'Retire';
  return `Your note says '${other}' — did you mean ${title}?`;
}

/** True only when everything asked for was written and its audit trail posted. */
export function isClean(c: ResultCounts, asked: number): boolean {
  return c.applied === asked && !c.conflicts && !c.failed && !c.refused && !c.auditPending;
}
